import { COLOURWAYS, type ColourwayId } from '@/components/brand/BrandArtwork';

/**
 * Finish selector — BUILD-PLAN §14 (variants), §40, §44.
 *
 * Selecting a swatch re-renders the product artwork in that colourway, so the
 * customer sees the finish applied rather than described. That immediacy is
 * the whole point: a colour name means little, a picture of the box means
 * everything.
 *
 * Accessibility notes, since colour-only controls are a common failure:
 *   • each swatch is a real <button> in a radiogroup, so it is reachable and
 *     operable by keyboard
 *   • the finish name is in the accessible name, never conveyed by the colour
 *     alone (§40 — colour is not information)
 *   • selection is shown by a ring *and* by the visible label beneath, so it
 *     does not depend on perceiving the swatch colour
 */

interface ColourwaySwatchesProps {
  value: ColourwayId;
  onChange: (id: ColourwayId) => void;
  /** Restrict to the finishes a given product actually offers. */
  available?: readonly ColourwayId[];
  size?: 'sm' | 'md';
  /** Hide the label row on dense layouts such as product cards. */
  showLabel?: boolean;
}

export function ColourwaySwatches({
  value,
  onChange,
  available,
  size = 'md',
  showLabel = true,
}: ColourwaySwatchesProps) {
  const options = available
    ? COLOURWAYS.filter((colourway) => available.includes(colourway.id))
    : COLOURWAYS;

  if (options.length <= 1) {
    // A single choice is not a choice — §86, no controls that do nothing.
    return null;
  }

  /*
   | The `sm` swatch is responsive, not a fixed 24px.
   |
   | Six swatches at 24px with a 10px gap need 204px of row. A product card on
   | a 390px phone is about 175px wide, so the set wrapped onto a second row
   | and added ~40px to every card in the grid — which is a large part of why
   | only two products fitted on a phone screen. At 20px with a 6px gap the
   | six fit one row, and the full size returns as soon as there is room.
   */
  const dimension = size === 'sm' ? 'h-5 w-5 lg:h-6 lg:w-6' : 'h-9 w-9';
  const selected = options.find((option) => option.id === value);

  return (
    <div className="flex flex-col gap-2.5">
      <div
        role="radiogroup"
        aria-label="Box finish"
        className={[
          'flex flex-wrap items-center',
          size === 'sm' ? 'gap-1.5 lg:gap-2.5' : 'gap-2.5',
        ].join(' ')}
      >
        {options.map((option) => {
          const isSelected = option.id === value;

          return (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={option.label}
              title={option.label}
              onClick={() => onChange(option.id)}
              className={`${dimension} rounded-full transition-all duration-300 ${
                isSelected
                  ? 'ring-2 ring-accent ring-offset-2 ring-offset-background'
                  : 'ring-1 ring-line hover:ring-ink/40'
              }`}
              style={{
                // A two-stop gradient previews the ground and its foil in one
                // dot, which a flat colour chip cannot convey.
                background: `linear-gradient(135deg, ${option.base} 0%, ${option.base} 58%, ${option.gold} 58%, ${option.gold} 100%)`,
              }}
            />
          );
        })}
      </div>

      {showLabel && selected && (
        // aria-live so keyboard and screen-reader users hear the change too.
        <p className="text-xs text-muted" aria-live="polite">
          Finish: <span className="text-ink">{selected.label}</span>
        </p>
      )}
    </div>
  );
}
